import {
  CanonicalRelationshipReviewContractError,
  assertCanonicalRelationshipReviewLimit,
  assertCanonicalRelationshipReviewUuid,
  parseCanonicalRelationshipReviewDocumentDecisionResult,
  parseCanonicalRelationshipReviewDocumentDetail,
  parseCanonicalRelationshipReviewDocumentHistory,
  parseCanonicalRelationshipReviewDocumentPreview,
  parseCanonicalRelationshipDecisionResult,
  parseCanonicalRelationshipReviewDetail,
  parseCanonicalRelationshipReviewHistory,
  parseCanonicalRelationshipReviewQueue,
  validateCanonicalRelationshipDecisionInput,
  type CanonicalRelationshipDecision,
  type CanonicalRelationshipDecisionResult,
  type CanonicalRelationshipReviewDocumentDecisionResult,
  type CanonicalRelationshipReviewDocumentDetail,
  type CanonicalRelationshipReviewDocumentHistory,
  type CanonicalRelationshipReviewDocumentPreview,
  type CanonicalRelationshipReviewDetail,
  type CanonicalRelationshipReviewHistory,
  type CanonicalRelationshipReviewQueue,
} from "@/data/contractor/compliance/canonicalRelationshipReview";
import { isCanonicalInternalComplianceReviewEnabled } from "./individual-compliance-ops-review.service";
import {
  CanonicalInternalReviewReadClientError,
  assertCanonicalInternalReviewInputKeys,
  canonicalInternalReviewFeatureDisabled,
  defaultCanonicalInternalReviewInvoker,
  parseCanonicalInternalReviewResult,
  type CanonicalInternalReviewFunctionInvoker,
} from "./canonical-internal-review-read.shared";

export const RELATIONSHIP_REVIEW_OPS_REVIEW_READ_FUNCTION =
  "relationship-review-ops-review-read";
export const RELATIONSHIP_REVIEW_OPS_REVIEW_DECIDE_FUNCTION =
  "relationship-review-ops-review-decide";
export const RELATIONSHIP_REVIEW_OPS_REVIEW_PREVIEW_FUNCTION =
  "relationship-review-ops-review-read-url";

export type ListRelationshipReviewQueueInput = Readonly<{
  organizationId: string;
  parentContractorId: string;
  limit?: number;
}>;

export type GetRelationshipReviewDetailInput = Readonly<{
  organizationId: string;
  parentContractorId: string;
  reviewId: string;
}>;

export type GetRelationshipReviewHistoryInput =
  GetRelationshipReviewDetailInput;

export type RelationshipReviewDocumentIdentityInput = Readonly<{
  organizationId: string;
  parentContractorId: string;
  reviewId: string;
  relationshipRequirementId: string;
  documentId: string;
}>;

export type GetRelationshipReviewDocumentDetailInput =
  RelationshipReviewDocumentIdentityInput;

export type GetRelationshipReviewDocumentHistoryInput =
  RelationshipReviewDocumentIdentityInput;

export type GetRelationshipReviewDocumentPreviewInput =
  RelationshipReviewDocumentIdentityInput & Readonly<{
    expiresInSeconds?: number;
  }>;

export type SubmitRelationshipReviewDocumentDecisionInput =
  RelationshipReviewDocumentIdentityInput & Readonly<{
    decision: CanonicalRelationshipDecision;
    reasonCode?: string;
    note?: string;
    idempotencyKey: string;
    expectedLatestDecisionId?: string | null;
  }>;

export type SubmitRelationshipReviewDecisionInput = Readonly<{
  organizationId: string;
  parentContractorId: string;
  reviewId: string;
  decision: CanonicalRelationshipDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
  expectedLatestDecisionId?: string | null;
}>;

export function createRelationshipReviewOpsReviewReadClient(dependencies?: {
  invoke?: CanonicalInternalReviewFunctionInvoker;
  enabled?: () => boolean;
}) {
  const invoke = dependencies?.invoke ?? defaultCanonicalInternalReviewInvoker;
  const enabled =
    dependencies?.enabled ?? isCanonicalInternalComplianceReviewEnabled;

  function assertEnabled(): void {
    if (!enabled()) {
      canonicalInternalReviewFeatureDisabled(
        "relationship_review_ops_feature_disabled",
      );
    }
  }

  async function invokeRead<T>(
    body: Readonly<Record<string, unknown>>,
    parser: (value: unknown) => T,
    functionName = RELATIONSHIP_REVIEW_OPS_REVIEW_READ_FUNCTION,
  ): Promise<T> {
    try {
      return parseCanonicalInternalReviewResult(
        await invoke(functionName, { body }),
        parser,
        "relationship_review_ops_",
      );
    } catch (error) {
      if (error instanceof CanonicalInternalReviewReadClientError) throw error;
      if (error instanceof CanonicalRelationshipReviewContractError) {
        throw new CanonicalInternalReviewReadClientError(
          "unexpected_error",
          error.code,
          null,
        );
      }
      throw error;
    }
  }

  function documentIdentity(
    input: RelationshipReviewDocumentIdentityInput,
  ): Readonly<{
    organizationId: string;
    parentContractorId: string;
    reviewId: string;
    relationshipRequirementId: string;
    documentId: string;
  }> {
    return Object.freeze({
      organizationId:
        assertCanonicalRelationshipReviewUuid(input.organizationId),
      parentContractorId:
        assertCanonicalRelationshipReviewUuid(input.parentContractorId),
      reviewId: assertCanonicalRelationshipReviewUuid(input.reviewId),
      relationshipRequirementId: assertCanonicalRelationshipReviewUuid(
        input.relationshipRequirementId,
      ),
      documentId: assertCanonicalRelationshipReviewUuid(input.documentId),
    });
  }

  return Object.freeze({
    async listQueue(
      input: ListRelationshipReviewQueueInput,
    ): Promise<CanonicalRelationshipReviewQueue> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "parentContractorId", "limit"],
        "relationship_review_ops_request_invalid",
      );
      const body = Object.freeze({
        operation: "list_queue",
        organizationId:
          assertCanonicalRelationshipReviewUuid(input.organizationId),
        parentContractorId:
          assertCanonicalRelationshipReviewUuid(input.parentContractorId),
        ...(input.limit === undefined
          ? {}
          : { limit: assertCanonicalRelationshipReviewLimit(input.limit) }),
      });
      return invokeRead(body, parseCanonicalRelationshipReviewQueue);
    },

    async getDetail(
      input: GetRelationshipReviewDetailInput,
    ): Promise<CanonicalRelationshipReviewDetail> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "parentContractorId", "reviewId"],
        "relationship_review_ops_request_invalid",
      );
      const body = Object.freeze({
        operation: "get_detail",
        organizationId:
          assertCanonicalRelationshipReviewUuid(input.organizationId),
        parentContractorId:
          assertCanonicalRelationshipReviewUuid(input.parentContractorId),
        reviewId: assertCanonicalRelationshipReviewUuid(input.reviewId),
      });
      return invokeRead(body, parseCanonicalRelationshipReviewDetail);
    },

    async getHistory(
      input: GetRelationshipReviewHistoryInput,
    ): Promise<CanonicalRelationshipReviewHistory> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(
        input,
        ["organizationId", "parentContractorId", "reviewId"],
        "relationship_review_ops_request_invalid",
      );
      const body = Object.freeze({
        operation: "get_history",
        organizationId:
          assertCanonicalRelationshipReviewUuid(input.organizationId),
        parentContractorId:
          assertCanonicalRelationshipReviewUuid(input.parentContractorId),
        reviewId: assertCanonicalRelationshipReviewUuid(input.reviewId),
      });
      return invokeRead(body, parseCanonicalRelationshipReviewHistory);
    },

    async getDocumentDetail(
      input: GetRelationshipReviewDocumentDetailInput,
    ): Promise<CanonicalRelationshipReviewDocumentDetail> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(input, [
        "organizationId", "parentContractorId", "reviewId",
        "relationshipRequirementId", "documentId",
      ], "relationship_review_ops_document_request_invalid");
      return invokeRead(
        Object.freeze({
          operation: "get_document_detail",
          ...documentIdentity(input),
        }),
        parseCanonicalRelationshipReviewDocumentDetail,
      );
    },

    async getDocumentHistory(
      input: GetRelationshipReviewDocumentHistoryInput,
    ): Promise<CanonicalRelationshipReviewDocumentHistory> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(input, [
        "organizationId", "parentContractorId", "reviewId",
        "relationshipRequirementId", "documentId",
      ], "relationship_review_ops_document_request_invalid");
      return invokeRead(
        Object.freeze({
          operation: "get_document_history",
          ...documentIdentity(input),
        }),
        parseCanonicalRelationshipReviewDocumentHistory,
      );
    },

    async getDocumentPreview(
      input: GetRelationshipReviewDocumentPreviewInput,
    ): Promise<CanonicalRelationshipReviewDocumentPreview> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(input, [
        "organizationId", "parentContractorId", "reviewId",
        "relationshipRequirementId", "documentId", "expiresInSeconds",
      ], "relationship_review_ops_document_request_invalid");
      const expiresInSeconds = input.expiresInSeconds ?? 300;
      if (
        !Number.isInteger(expiresInSeconds)
        || expiresInSeconds < 30
        || expiresInSeconds > 600
      ) {
        throw new CanonicalInternalReviewReadClientError(
          "invalid_request",
          "relationship_review_ops_preview_invalid",
          400,
        );
      }
      return invokeRead(
        Object.freeze({
          operation: "create_signed_preview",
          ...documentIdentity(input),
          expiresInSeconds,
        }),
        parseCanonicalRelationshipReviewDocumentPreview,
        RELATIONSHIP_REVIEW_OPS_REVIEW_PREVIEW_FUNCTION,
      );
    },

    async submitDocumentDecision(
      input: SubmitRelationshipReviewDocumentDecisionInput,
    ): Promise<CanonicalRelationshipReviewDocumentDecisionResult> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(input, [
        "organizationId", "parentContractorId", "reviewId",
        "relationshipRequirementId", "documentId", "decision", "reasonCode",
        "note", "idempotencyKey", "expectedLatestDecisionId",
      ], "relationship_review_ops_document_request_invalid");
      const normalized = validateCanonicalRelationshipDecisionInput({
        decision: input.decision,
        reasonCode: input.reasonCode,
        note: input.note,
        idempotencyKey: input.idempotencyKey,
      });
      return invokeRead(
        Object.freeze({
          operation: "append_document_decision",
          ...documentIdentity(input),
          decision: normalized.decision,
          ...(normalized.reasonCode
            ? { reasonCode: normalized.reasonCode }
            : {}),
          ...(normalized.note ? { note: normalized.note } : {}),
          idempotencyKey: normalized.idempotencyKey,
          expectedLatestDecisionId:
            input.expectedLatestDecisionId == null
              ? null
              : assertCanonicalRelationshipReviewUuid(
                input.expectedLatestDecisionId,
              ),
        }),
        parseCanonicalRelationshipReviewDocumentDecisionResult,
        RELATIONSHIP_REVIEW_OPS_REVIEW_DECIDE_FUNCTION,
      );
    },

    async submitDecision(
      input: SubmitRelationshipReviewDecisionInput,
    ): Promise<CanonicalRelationshipDecisionResult> {
      assertEnabled();
      assertCanonicalInternalReviewInputKeys(input, [
        "organizationId", "parentContractorId", "reviewId", "decision",
        "reasonCode", "note", "idempotencyKey", "expectedLatestDecisionId",
      ], "relationship_review_ops_request_invalid");
      const normalized = validateCanonicalRelationshipDecisionInput({
        decision: input.decision,
        reasonCode: input.reasonCode,
        note: input.note,
        idempotencyKey: input.idempotencyKey,
      });
      const body = Object.freeze({
        operation: "append_decision",
        organizationId:
          assertCanonicalRelationshipReviewUuid(input.organizationId),
        parentContractorId:
          assertCanonicalRelationshipReviewUuid(input.parentContractorId),
        reviewId: assertCanonicalRelationshipReviewUuid(input.reviewId),
        decision: normalized.decision,
        ...(normalized.reasonCode ? { reasonCode: normalized.reasonCode } : {}),
        ...(normalized.note ? { note: normalized.note } : {}),
        idempotencyKey: normalized.idempotencyKey,
        expectedLatestDecisionId:
          input.expectedLatestDecisionId == null
            ? null
            : assertCanonicalRelationshipReviewUuid(
              input.expectedLatestDecisionId,
            ),
      });
      return invokeRead(
        body,
        parseCanonicalRelationshipDecisionResult,
        RELATIONSHIP_REVIEW_OPS_REVIEW_DECIDE_FUNCTION,
      );
    },
  });
}
